// Copyright 1999-2024. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { MenuItem } from '@plesk/ui-library';
import { INTENT_TYPE } from 'common/constants';
import * as responseErrorActions from 'common/modules/app/responseError/actions';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import { connect } from 'react-redux';
import { RootState } from 'client/core/store';
import ConfirmationInputPopover,
{ IConfirmationInputTranslation } from 'common/components/ConfirmationInputPopover/ConfirmationInputPopover';
import { TEST } from 'common/components/ButtonWithInputConfirmation/ButtonWithInputConfirmation';

interface IMenuItemWithInputConfirmationProps extends React.HTMLProps<HTMLDivElement> {
    onClose?: () => void;
    menuItemText?: string | React.ReactNode;
    disabled?: boolean;
    icon?: string | JSX.Element;
    isLoading?: boolean;
    handleConfirm: () => void;
    translations: IConfirmationInputTranslation;
    'data-cy'?: string;
    confirmation: string;
    placement?: string;
}

export type MenuItemWithInputConfirmationProps =
    IMenuItemWithInputConfirmationProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const MenuItemWithInputConfirmation: React.FC<MenuItemWithInputConfirmationProps> = ({
    responseErrorActions: { clearResponseError },
    menuItemText,
    onClose,
    error,
    icon,
    handleConfirm,
    isLoading,
    disabled,
    translations: {
        label,
        button,
        text,
        title,
    },
    'data-cy': dataCy = TEST.INPUT_CONFIRM_POPOVER,
    confirmation,
    placement,
}) => {
    const [isPopoverOpened, setPopoverOpened] = React.useState(false);

    const handleClose = () => {
        setPopoverOpened(false);
        clearResponseError();

        onClose?.();
    };

    const handleOpenPopover = () => {
        clearResponseError();
        setPopoverOpened(true);
    };

    const handleClickConfirm = async () => {
        setPopoverOpened(false);

        await handleConfirm();

        if (!error.error) {
            setPopoverOpened(false);
        }
    };

    return (
        <ConfirmationInputPopover
            opened={isPopoverOpened}
            data-cy={dataCy}
            translations={{
                label,
                button,
                text,
                title,
            }}
            disabled={isLoading}
            error={error.error ? error.error : undefined}
            intent={error.error ? INTENT_TYPE.DANGER : null}
            isLoading={isLoading}
            handleConfirm={handleClickConfirm}
            onClose={handleClose}
            confirmation={confirmation}
            placement={placement}
        >
            <MenuItem
                onClick={handleOpenPopover}
                icon={icon}
                disabled={disabled}
            >
                {menuItemText}
            </MenuItem>
        </ConfirmationInputPopover>
    );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    responseErrorActions: bindActionCreators(responseErrorActions, dispatch),
});

const mapStateToProps = (state: RootState) => ({
    error: state.app.responseError,
});

export default connect(mapStateToProps, mapDispatchToProps)(MenuItemWithInputConfirmation);
